<template>
    <div class="message-container" :class="{ 'user-message': isUser, 'bot-message': !isUser }">
        <div class="flex items-start" :class="{ 'justify-end': isUser }">
            <!-- Bot Avatar (only shown for bot messages) -->
            <div v-if="!isUser" class="flex-shrink-0 mr-3">
                <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                    <svg class="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
                    </svg>
                </div>
            </div>

            <!-- Message content -->
            <div class="message-content" :class="{ 'items-end': isUser }">
                <div class="message-bubble max-w-[75%]" :class="[
          isUser ? 'bg-blue-700 text-white' : 'bg-white border border-gray-200',
          isUser ? 'rounded-tl-lg rounded-bl-lg rounded-tr-lg' : 'rounded-tr-lg rounded-br-lg rounded-tl-lg',
          isUser ? 'ml-auto' : ''
        ]">
                    <div class="p-3">
                        <div class="text-sm min-width-1 ai-content" :class="{ 'text-gray-800': !isUser }"
                             v-html="message.content"></div>

                        <!-- Display metadata if available -->
                        <div v-if="message.metadata && !isUser"
                             class="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                            <div v-if="message.metadata.sources && message.metadata.sources.length > 0">
                                <div class="font-medium mb-1">Sources:</div>
                                <ul class="list-disc pl-4">
                                    <li v-for="(source, index) in message.metadata.sources" :key="index">
                                        {{ source }}
                                    </li>
                                </ul>
                            </div>

                            <div v-if="message.metadata.confidence" class="mt-1">
                                Confidence: {{ (message.metadata.confidence * 100).toFixed(1) }}%
                            </div>
                        </div>
                    </div>
                </div>
                <div class="text-xs text-gray-400 mt-1 px-1">
                    {{ formattedTime }}
                </div>
            </div>

            <!-- User Avatar (only shown for user messages) -->
            <div v-if="isUser" class="flex-shrink-0 ml-3">
                <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
          <span v-if="!userAvatar" class="text-sm font-semibold text-gray-600">
            {{ userInitials }}
          </span>
                    <img v-else :src="userAvatar" alt="User avatar" class="h-full w-full object-cover"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, PropType, computed} from 'vue';
import {ChatMessage} from '@/types/chat';

export default defineComponent({
    name: 'ChatbotMessage',
    props: {
        message: {
            type: Object as PropType<ChatMessage>,
            required: true
        },
        isUser: {
            type: Boolean,
            default: false
        },
        userAvatar: {
            type: String,
            default: null
        },
        userName: {
            type: String,
            default: 'User'
        }
    },
    setup(props) {
        // Format the timestamp
        const formattedTime = computed(() => {
            if (!props.message.created_at) return '';

            const date = new Date(props.message.created_at);
            return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
        });

        // Compute user initials from name
        const userInitials = computed(() => {
            if (!props.userName) return 'U';
            return props.userName
                .split(' ')
                .map(name => name.charAt(0))
                .join('')
                .toUpperCase();
        });

        return {
            formattedTime,
            userInitials
        };
    }
});
</script>

<style>
/* Global styles for AI-generated content (not scoped to allow styling of v-html content) */
.ai-content h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    color: #000091;
}

.ai-content > div {
    margin: 0 !important;
    padding: 0.5rem !important;
    background: transparent !important;
    background-color: transparent !important;
    border: none !important;
}

.ai-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    color: #000091;
}

.ai-content h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
    color: #000091;
}

.ai-content p {
    margin-bottom: 0.75rem;
    line-height: 1.5;
}

.ai-content ul, .ai-content ol {
    margin-top: 0.5rem;
    margin-bottom: 0.75rem;
    padding-left: 1.5rem;
}

.ai-content ul {
    list-style-type: disc;
}

.ai-content ol {
    list-style-type: decimal;
}

.ai-content li {
    margin-bottom: 0.25rem;
}

.ai-content code {
    font-family: monospace;
    background-color: #f1f1f1;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    font-size: 0.9em;
}

.ai-content pre {
    background-color: #f5f5f5;
    padding: 0.75rem;
    border-radius: 4px;
    overflow-x: auto;
    margin: 0.75rem 0;
}

.ai-content pre code {
    background-color: transparent;
    padding: 0;
}

.ai-content strong {
    font-weight: 600;
}

.ai-content em {
    font-style: italic;
}

.ai-content a {
    color: #000091;
    text-decoration: underline;
}

.ai-content a:hover {
    text-decoration: none;
}

.ai-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
}

.ai-content th, .ai-content td {
    border: 1px solid #e2e8f0;
    padding: 0.5rem;
    text-align: left;
}

.ai-content th {
    background-color: #f8fafc;
    font-weight: 600;
}

.ai-content tr:nth-child(even) {
    background-color: #f9fafb;
}
</style>

<style scoped>
.message-container {
    margin-bottom: 16px;
}

.min-width-1 {
    min-width: 3rem;
}

.message-content {
    display: flex;
    flex-direction: column;
    max-width: 80%;
}

.message-bubble {
    max-width: fit-content;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    position: relative;
}

.user-message .message-content {
    align-items: flex-end;
}

.bot-message .message-content {
    align-items: flex-start;
}

/* Animation for messages */
.message-container {
    animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>